// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

/**
 * AEM alarm-api → UI `Alarm` adapter.
 *
 * This is the single seam between the flat AEM record (`AlarmApiRecord`, one row
 * of `dbo.alarm_event_state`) and the operator-console `Alarm` domain model the
 * cards render. Only fields the AEM OpenAPI spec (docs/upstream-specs/alarm-api.yaml)
 * actually carries are populated from real data; everything else is left
 * `undefined` / `'Unknown'` rather than guessed, so the UI never presents
 * fabricated content as if it came from the source system.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * MISSING FIELDS — AEM does not carry these; they are left `undefined`/`'Unknown'`
 * below and marked with `TODO(aem):`. Fill each in as its source becomes
 * available so the swap to real data stays a one-file change.
 *
 *   • sev (P1–P4)        the event's own `priority`, else the alarm rule's (via
 *                        the `resolveSeverity` hook), else 'Unknown' — a real
 *                        severity value with its own chip that sorts last, NOT a
 *                        guessed P1. Note tab membership keys off `validated`
 *                        and `closed`, not severity, so an Unknown alarm still
 *                        surfaces under Alarms.
 *   • group/domain       `equipment_type` is used verbatim (no curated taxonomy
 *                        mapping — that would itself be fabricated data); absent
 *                        when the record has none, so the alarm only shows under
 *                        the "All" pill rather than a guessed domain.
 *   • time (HH:MM)       only `acknowledged_at` (a timestamp, and only once
 *                        acked) → formatted best-effort; true raise-time absent.
 *   • raisedAgo/elapsed  no raise timestamp → left undefined (card hides the
 *                        "… ago" clause rather than showing a fake "0:00 ago").
 *   • validated/stage    no validation concept; `alarm_status` is free-text per
 *                        the spec → best-effort lifecycle mapping. `validated`
 *                        defaults true so AEM alarms surface under Alarms (the
 *                        most-severe bucket) until real severity data exists.
 *   • dxTag/summary      no short diagnosis/summary → SOP title only.
 *   • issue/diagnosis    only from the linked SOP payload.
 *   • quality            no data-quality flag → undefined (no suspect marker).
 *   • confidence         no confidence field → 'Unknown' unless a SOP is linked.
 *   • kind               AEM is alarms only; routine/optwork/task come from other
 *                        upstreams (scheduler / optimization engine / planner).
 *   • causes, telemetry, upstream/downstream, floorCells, related, auditTrail
 *                        detail-panel richness with no AEM source → empty.
 * ─────────────────────────────────────────────────────────────────────────────
 */

import type { Alarm, AlarmStep, Confidence, ResolvedSop, Severity } from '../types/alarm';
import { isAckedState, isClosedState, needsAckState, stateLabel } from '../types/alarmFsm';
import type { AlarmApiRecord, AlarmRule } from '../types/alarmApi';
import type { Artifact, SopStep } from '../types/sopFactory';
import { minutesSince } from '../utils/time';
import { manualSopIdForRules } from './manualSopMap';

/** Optional enrichment hooks that fill fields AEM cannot supply on its own. */
export interface AlarmEnrichment {
  /**
   * Resolve a severity for a record. Defaults to 'P1' (most severe) when no
   * priority is known — an unknown alarm is treated as urgent until proven otherwise.
   * Backed by the alarm-rule registry's `priority` (see useAlarms.loadAlarmWithSop).
   */
  resolveSeverity?: (_record: AlarmApiRecord) => Severity;
}

/**
 * All SOP ids linked to an alarm, in display precedence and deduped:
 * the rule's related SOP documents, then the hardcoded manual-map fallback
 * (keyed on `alarm_code`). `[0]` is the displayed SOP; the full list feeds
 * Ask Helios `bound_sops`.
 */
export function resolveSopIds(record: AlarmApiRecord, rule?: AlarmRule | null): string[] {
  // Every related document is a candidate, with no filter on `document_type`.
  // That used to be `=== 'SOP'`, but the alarm-api contract now pins
  // `RelatedDocumentBody.document_type` to `const: OTHER` — the old
  // SOP/MOP/EOP/GOV/ARP vocabulary is gone, and `related_documents` itself is a
  // deprecated read-only projection of the `alarm_rule --HAS_DOCUMENT--> document`
  // edges in the artifact-relationship registry. Filtering on 'SOP' therefore
  // matched nothing against the real service and silently emptied the Response
  // procedure card. A non-SOP id here just 404s in the SOP Factory and degrades
  // to a 'failed' entry, which is the same handling a missing SOP already got.
  // TODO(aem): read the edges from /v1/sites/{site_id}/relationships directly —
  // `related_documents` is slated for removal once callers have migrated.
  const ruleSopIds = (rule?.related_documents ?? []).map(d => d.document_id);
  const manual = manualSopIdForRules(record.alarm_code ? [record.alarm_code] : undefined);
  return Array.from(new Set([...ruleSopIds, ...(manual ? [manual] : [])].filter(Boolean)));
}

/**
 * Fallback severity when none is known.
 *
 * 'Unknown' rather than 'P1'. Defaulting to the most severe bucket was meant to
 * avoid under-triage, but it made an unknown indistinguishable from a genuine
 * critical: the card showed a red P1 chip, the alarm claimed a 30s SLA it had no
 * basis for, and the fleet-wide "open escalations" P1 count was inflated by every
 * alarm whose rule simply isn't registered. 'Unknown' is already a first-class
 * `Severity` with its own chip, styling and `order: 5` (see theme/severity.ts),
 * so these now read as what they are and sort last.
 */
const DEFAULT_SEVERITY: Severity = 'Unknown';

/**
 * Map a `priority` to a `Severity`. Accepts both the alarm event's bare-digit form
 * ("1".."4", AEM 0.2.0) and the alarm rule's conventional "P1".."P4" form; digits
 * are normalized to the P-prefixed value. Case-insensitive; anything
 * unrecognized/absent → 'Unknown' (see DEFAULT_SEVERITY).
 */
export function priorityToSeverity(priority: string | null | undefined): Severity {
  const raw = priority?.trim().toUpperCase();
  const p = raw && /^[1-4]$/.test(raw) ? `P${raw}` : raw;
  return p === 'P1' || p === 'P2' || p === 'P3' || p === 'P4' ? (p as Severity) : DEFAULT_SEVERITY;
}

/**
 * Split one SOP step into verb + instruction. They stay separate rather than
 * being joined into a `VERB — instruction` line: the alarm detail renders the
 * verb as its own chip and keys the step's treatment off it.
 */
function toAlarmStep(step: SopStep): AlarmStep {
  return {
    verb: step.verb?.trim() || undefined,
    instruction: (step.instruction ?? '').trim(),
  };
}

/**
 * Fold a resolved SOP Factory artifact into the UI `ResolvedSop` the Response
 * procedure card renders. Steps with neither a verb nor an instruction are
 * dropped. Used by the alarm hook (per-SOP, for the parallel fetch) and by the
 * mapper's legacy single-artifact path.
 */
export function artifactToResolvedSop(id: string, sop: Artifact): ResolvedSop {
  const steps = (sop.payload?.steps ?? [])
    .map(toAlarmStep)
    .filter(s => Boolean(s.instruction || s.verb));
  const durationHours = sop.payload?.estimated_duration_hours;
  return {
    id,
    status: 'loaded',
    title: sop.title,
    version: sop.version,
    labor: typeof durationHours === 'number' ? `~${durationHours}h` : undefined,
    diagnosis: sop.payload?.description_of_work,
    steps,
  };
}

/**
 * Stable list key for a record. `alarm_code` alone collides for anomalies, so
 * every identifying column the payload actually carries is folded in. Findings
 * arrive with `point_key`/`alarm_status`/`assigned_to` null today, so this is
 * the most that can be distinguished from the response.
 */
export function alarmRecordUid(record: AlarmApiRecord): string {
  return [
    record.alarm_code,
    record.site_id,
    record.equipment_id ?? record.equipment_key,
    record.point_key,
    record.event_timestamp,
  ]
    .map(part => part ?? '')
    .join('|');
}

/**
 * Build the `Alarm` shape from an AEM record plus (optionally) the resolved SOP
 * artifact, the alarm rule looked up by `alarm_code` (no FK guarantee — may be
 * null when no rule is registered), and enrichment hooks for fields AEM cannot
 * supply.
 */
export function alarmApiRecordToAlarm(
  record: AlarmApiRecord,
  sops?: Artifact | ResolvedSop[] | null,
  enrich: AlarmEnrichment = {},
  rule?: AlarmRule | null
): Alarm {
  // rule related SOP docs → manual map, deduped. [0] is the first *linked* id;
  // the full list feeds Ask Helios bound_sops.
  const sopIds = resolveSopIds(record, rule);
  const sopId = sopIds[0] ?? '';

  // Accept either the new list of resolved SOPs (from the parallel fetch) or a
  // single Artifact (legacy single-SOP callers/tests). The legacy scalar fields
  // below derive from the first *loaded* SOP — which may differ from `sopId`
  // (the first *linked* id) when an earlier SOP failed to load. The accordion
  // uses each item's own id, so `sopId` is no longer rendered but stays
  // populated for pageAlarmContext/tests.
  const resolvedSops: ResolvedSop[] = Array.isArray(sops)
    ? sops
    : sops
      ? [artifactToResolvedSop(sopId, sops)]
      : [];
  const firstLoaded = resolvedSops.find(s => s.status === 'loaded');

  const steps: AlarmStep[] = firstLoaded?.steps ?? [];
  const labor = firstLoaded?.labor;

  const asset = record.equipment_key ?? record.equipment_id ?? 'Unknown';
  const issue = firstLoaded?.title
    ? `${record.alarm_code ?? 'Alarm'} — ${firstLoaded.title}`
    : (record.alarm_code ?? 'Unknown');

  // Severity: prefer the alarm event's own `priority` (AEM 0.2.0), falling back
  // to the enrichment hook (the alarm rule's priority) when the event has none,
  // then to P1 (most severe) when nothing is known.
  const sev = record.priority
    ? priorityToSeverity(record.priority)
    : (enrich.resolveSeverity?.(record) ?? DEFAULT_SEVERITY);

  // equipment_type used verbatim — no curated Cooling/Power/… taxonomy, since
  // that mapping would itself be fabricated. Absent when the record has none.
  const group = record.equipment_type ?? undefined;

  // alarm_status is a state name from the row's bound state machine — see
  // types/alarmFsm.ts. Compared verbatim, not lowercased: the machines emit
  // exactly these uppercase names, and normalizing would only mask a value that
  // is not actually a state.
  const status = record.alarm_status ?? null;
  const closed = isClosedState(status);
  const acked = isAckedState(status);

  const confidence: Confidence = firstLoaded ? 'Medium' : 'Unknown';

  return {
    id: record.alarm_code ?? 'Unknown',
    uid: alarmRecordUid(record),
    alarmId: record.alarm_id ?? undefined,
    // No FK to the rule registry: the resolved rule's id, else alarm_code by convention.
    ruleId: rule?.rule_id ?? record.alarm_code ?? undefined,
    point: record.point_key ?? undefined,
    sev,
    // event_timestamp is the true raise time (AEM 0.2.0); fall back to
    // acknowledged_at (only present once acked) when the event has none.
    eventTimestamp: record.event_timestamp ?? record.acknowledged_at ?? undefined,
    raisedAgo: minutesSince(record.event_timestamp), // undefined when no raise time.
    asset,
    // Machine id — used for the twin-graph topology + telemetry lookups, which key
    // on equipment_id, not the human key. The readable name is `asset` above.
    assetId: record.equipment_id ?? undefined,
    location: record.site_key ?? 'Unknown',
    siteId: record.site_id ?? '',
    orgId: record.organization_id ?? undefined,
    group,
    src: undefined, // TODO(aem): no source-system field; the detail 'Source' uses rule category.
    quality: undefined, // TODO(aem): no typed data-quality flag in AEM.
    kind: 'alarm', // TODO(aem): routine/optwork/task come from other upstreams.
    statusChip: stateLabel(status),
    status: status ?? undefined,
    dxTag: firstLoaded?.title ?? undefined, // TODO(aem): no short diagnosis label outside a linked SOP.
    summary: firstLoaded?.title ?? 'Unknown',
    issue,
    diagnosis: firstLoaded?.diagnosis ?? 'Unknown', // TODO(aem): from SOP only.
    confidence, // TODO(aem): no confidence field outside a linked SOP.
    sopId,
    sopIds,
    sops: resolvedSops,
    sopVersion: firstLoaded?.version,
    labor,
    steps,
    telemetry: [], // TODO(aem): no telemetry in AEM (comes from a metrics source).
    causes: [], // TODO(aem): no causal analysis in AEM.
    auditTrail: [], // TODO(aem): reconstruct from acknowledged_by/at + status history.
    owner: record.assigned_to ?? undefined,
    assignedAt: record.assigned_at ?? undefined,
    ownerStatus: acked ? 'Acknowledged' : undefined,
    acknowledgedBy: record.acknowledged_by ?? undefined,
    acknowledgedAt: record.acknowledged_at ?? undefined,
    closedAt: record.closed_at ?? undefined,
    closedBy: record.closed_by ?? undefined,
    workOrder: record.work_order ?? undefined,
    workOrderId: record.work_order_id ?? undefined,
    ruleTitle: rule?.title ?? undefined,
    // Event priority (AEM 0.2.0) wins over the rule's; feeds the Ask Helios context.
    priority: record.priority ?? rule?.priority ?? undefined,
    controlLayer: rule?.control_layer ?? undefined,
    category: rule?.category ?? undefined, // TODO(aem): alarm-api has no category column.
    // TODO(aem): no validation workflow in AEM. Default true so every non-closed
    // alarm surfaces under Alarms (the most-severe bucket) rather than Anomalies,
    // until real severity data lets alarms be sorted into the right bucket.
    validated: true,
    acked,
    needsAck: needsAckState(status),
    closed,
  };
}
